import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '@/firebase/config';
import { handleFirebaseAuthError } from '@/utils/firebaseErrorHandler';
import { complianceService, ComplianceTemplate } from '@/firebase/complianceService';
import { locationService } from '@/firebase/locationService';
import { taskCompletionService } from '@/firebase/taskCompletionService';
import { assetService } from '@/firebase/assetService';

export interface TemplateSnapshot {
  name?: string;
  name_es?: string; // Spanish translation of name
  category?: string;
  risk_level?: string;
  risk_weight?: number;
  evidence_types?: any[];
  pass_fail_criteria?: string;
  pass_fail_criteria_es?: string; // Spanish translation of pass_fail_criteria
  jurisdiction?: string;
  jurisdiction_notes?: string;
  jurisdiction_notes_es?: string; // Spanish translation of jurisdiction_notes
  owner_role?: string;
  lead_times_days?: number[];
  frequency?: string;
  mark_due_in_days?: number;
}

export interface Task {
  id: string;
  templateId: string;
  locationId: string;
  status: 'pending' | 'completed' | 'overdue' | 'pending_review';
  dueDate: any; // Firestore Timestamp
  createdAt: any; // Firestore Timestamp
  updatedAt: any; // Firestore Timestamp
  frequency: string;
  originalTemplateId: string;
  parentTaskId?: string;
  userId?: string;
  completedBy?: string;
  completedAt?: any; // Firestore Timestamp
  reviewedBy?: string;
  reviewedAt?: any; // Firestore Timestamp
  rejectedBy?: string; // Email of reviewer who rejected the task
  rejectedAt?: any; // Timestamp when task was rejected
  rejectionReason?: string; // Reason for rejection
  organizationId?: string; // Multi-tenant support
  templateSnapshot?: TemplateSnapshot | null;
  assetIds?: string[]; // Array of asset IDs this task is tagged to
  assignedUserIds?: string[]; // Array of user IDs explicitly assigned to this task
  assignedUserId?: string; // Single user ID for backward compatibility
  checklistContext?: {
    checklistId?: string;
    itemKey?: string;
    assetId?: string;
  };
  // Messaging integration fields
  messageContext?: {
    createdFromMessageId?: string; // Message ID that triggered task creation
    conversationId?: string; // Conversation ID where task was discussed
    lastDiscussedAt?: any; // Firestore timestamp
  };
  // Reference images for task context
  referenceImageUrls?: string[]; // URLs of reference images
  referenceImageFileNames?: string[]; // Original filenames for reference images
  referenceImageMetadata?: Array<{
    url: string;
    fileName: string;
    uploadedAt: Date | number;
    uploadedBy: string;
    uploadedByName?: string;
    note?: string; // Optional note for the reference image
  }>;
}

export interface TaskWithDetails extends Task {
  templateName: string;
  locationName: string;
  locationState?: string;
  category: string;
  riskLevel: string;
  riskWeight?: number; // Risk weight for scoring calculations
  evidenceTypes: any[];
  passFailCriteria: string;
  jurisdiction: string;
  jurisdictionNotes: string;
  ownerRole: string;
  leadTimeDays?: number;
  completionDetails?: any;
  templateSnapshot?: TemplateSnapshot | null;
  assetName?: string; // Asset name when task is created from checklist with asset context
}

function computeRiskWeightFromLevel(riskLevel?: string): number {
  const normalized = (riskLevel || 'Medium').toUpperCase();
  if (normalized === 'HIGH') return 1.0;
  if (normalized === 'LOW') return 0.3;
  return 0.6;
}

function buildTemplateSnapshotFromTemplate(template: Partial<ComplianceTemplate>): TemplateSnapshot {
  const snapshot: TemplateSnapshot = {
    name: template.name,
    name_es: template.name_es, // Preserve Spanish translation
    category: template.category,
    risk_level: template.risk_level,
    risk_weight: template.risk_weight ?? computeRiskWeightFromLevel(template.risk_level),
    evidence_types: template.evidence_types,
    pass_fail_criteria: template.pass_fail_criteria,
    pass_fail_criteria_es: template.pass_fail_criteria_es, // Preserve Spanish translation
    jurisdiction: template.jurisdiction,
    jurisdiction_notes: template.jurisdiction_notes,
    jurisdiction_notes_es: template.jurisdiction_notes_es, // Preserve Spanish translation
    owner_role: template.owner_role,
    lead_times_days: template.lead_times_days,
    frequency: template.frequency,
    mark_due_in_days: template.mark_due_in_days,
  };
  return snapshot;
}

// Convert Firestore timestamp to Date
function convertTimestamp(timestamp: any): Date {
  if (timestamp && timestamp.toDate) {
    return timestamp.toDate();
  } else if (timestamp && timestamp._seconds) {
    return new Date(timestamp._seconds * 1000);
  } else if (timestamp instanceof Date) {
    return timestamp;
  } else if (typeof timestamp === 'number') {
    return new Date(timestamp);
  }
  return new Date();
}

// Get tasks for a specific organization (secure query)
export async function getTasksForOrganization(organizationId: string): Promise<TaskWithDetails[]> {
  try {
    if (!organizationId) {
      console.warn('⚠️ No organizationId provided to getTasksForOrganization');
      return [];
    }
    
    console.log(`📋 Fetching tasks for organization: ${organizationId}...`);
    
    // Query tasks by organizationId on the server side
    const tasksRef = collection(db, 'tasks');
    const tasksQuery = query(
      tasksRef, 
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );
    const tasksSnapshot = await getDocs(tasksQuery);
    
    const tasks: Task[] = [];
    tasksSnapshot.forEach((doc) => {
      tasks.push({ id: doc.id, ...doc.data() } as Task);
    });
    
    console.log(`📊 Found ${tasks.length} tasks for organization ${organizationId}`);
    
    // Get templates and locations for enrichment (filtered by organization)
    const templatesResult = await complianceService.getAllTemplates(organizationId);
    const locationsResult = await locationService.getAllLocations(organizationId);
    
    if (!templatesResult.success || !locationsResult.success) {
      console.error('Failed to fetch templates or locations');
      return [];
    }
    
    let templates = templatesResult.data || [];
    const locations = locationsResult.data || [];
    
    // Backward compatibility: ensure all templates have risk_weight
    const { calculateRiskWeight } = await import('@/firebase/complianceService');
    templates = templates.map(template => {
      if (template.risk_weight === undefined || template.risk_weight === null) {
        return {
          ...template,
          risk_weight: calculateRiskWeight(template.risk_level)
        };
      }
      return template;
    });

    const templateCache = new Map<string, (typeof templates[number] | TemplateSnapshot) | null>();
    const resolveTemplate = async (templateId?: string, snapshot?: TemplateSnapshot | null) => {
      if (!templateId) return null;
      if (templateCache.has(templateId)) {
        return templateCache.get(templateId) || null;
      }
      if (snapshot) {
        templateCache.set(templateId, snapshot);
        return snapshot;
      }
      let template = templates.find(t => t.id === templateId);
      if (!template) {
        try {
          const result = await complianceService.getTemplate(templateId);
          if (result.success && result.data) {
            template = result.data;
            templates.push(template);
            templateCache.set(templateId, template);
          }
        } catch (err) {
          console.warn(`Failed to fetch template ${templateId}:`, err);
        }
      }
      templateCache.set(templateId, template || null);
      return template || null;
    };
    
    // Helper function to update task status in database if needed
    const updateTaskStatusIfNeeded = async (taskId: string, newStatus: 'overdue') => {
      try {
        const taskRef = doc(db, 'tasks', taskId);
        await updateDoc(taskRef, {
          status: newStatus,
          updatedAt: serverTimestamp()
        });
        console.log(`✅ Updated task ${taskId} status to ${newStatus}`);
      } catch (error) {
        console.error(`Error updating task ${taskId} status:`, error);
      }
    };

    // Collect all assetIds from assetIds array and checklistContext for batch fetching
    const assetIdsToFetch = new Set<string>();
    tasks.forEach(task => {
      if (task.assetIds && Array.isArray(task.assetIds)) {
        task.assetIds.forEach((id: string) => assetIdsToFetch.add(id));
      }
      if (task.checklistContext?.assetId) {
        assetIdsToFetch.add(task.checklistContext.assetId);
      }
    });
    
    // Fetch all assets in batch
    const assetMap = new Map<string, string>();
    if (assetIdsToFetch.size > 0) {
      const assetFetchPromises = Array.from(assetIdsToFetch).map(async (assetId) => {
        try {
          const assetResult = await assetService.getAsset(assetId);
          if (assetResult.success && assetResult.data) {
            assetMap.set(assetId, assetResult.data.assetName || `Asset ${assetId}`);
          }
        } catch (error) {
          console.warn(`Failed to fetch asset ${assetId}:`, error);
        }
      });
      await Promise.all(assetFetchPromises);
    }

    // Enrich tasks with template and location details
    const now = new Date();
    const enrichedTasks: TaskWithDetails[] = [];
    
    for (const task of tasks) {
      const snapshot = task.templateSnapshot;
      let template = await resolveTemplate(task.templateId, snapshot);
      if (!template && task.originalTemplateId) {
        template = await resolveTemplate(task.originalTemplateId, snapshot);
      }
      const templateData = (template || snapshot) as (Partial<TemplateSnapshot> & Partial<ComplianceTemplate>) | undefined;
      const location = locations.find(l => l.id === task.locationId);
      
      const dueDate = convertTimestamp(task.dueDate);
      
      // Dynamically calculate status: if dueDate has passed and task is still pending, mark as overdue
      let taskStatus = task.status;
      if (taskStatus === 'pending' && dueDate < now) {
        taskStatus = 'overdue';
        // Update task in database if status needs to change (async, don't await to avoid blocking)
        updateTaskStatusIfNeeded(task.id, 'overdue').catch(err => {
          console.error(`Error updating task ${task.id} status to overdue:`, err);
        });
      }
      
      // Calculate risk weight for scoring
      const riskLevel = (templateData?.risk_level || template?.risk_level || 'Medium') as string;
      const riskWeight = templateData?.risk_weight ?? (template?.risk_weight ?? calculateRiskWeight(riskLevel));
      
      // Get user language preference for translation
      let language = 'en';
      try {
        // Try to get language from localStorage as fallback
        const storedLang = localStorage.getItem('preferredLanguage');
        if (storedLang === 'es') language = 'es';
      } catch (error) {
        // Ignore localStorage errors
      }
      
      // Get translated values based on language
      const templateName = getTranslatedField(
        templateData?.name || template?.name,
        templateData?.name_es || template?.name_es,
        language
      ) || `Template ${task.templateId}`;
      
      const passFailCriteria = getTranslatedField(
        templateData?.pass_fail_criteria || template?.pass_fail_criteria,
        templateData?.pass_fail_criteria_es || template?.pass_fail_criteria_es,
        language
      );
      
      const jurisdictionNotes = getTranslatedField(
        templateData?.jurisdiction_notes || template?.jurisdiction_notes,
        templateData?.jurisdiction_notes_es || template?.jurisdiction_notes_es,
        language
      );
      
      // Get asset name from checklistContext if available
      const primaryAssetId = (task.assetIds && task.assetIds[0]) || task.checklistContext?.assetId;
      const assetName = primaryAssetId ? assetMap.get(primaryAssetId) : undefined;
      
      enrichedTasks.push({
        ...task,
        status: taskStatus,
        templateName: templateName,
        locationName: location?.name || `Location ${task.locationId}`,
        locationState: location?.state,
        category: templateData?.category || template?.category || 'Compliance',
        riskLevel: (templateData?.risk_level || template?.risk_level || 'medium') as string,
        riskWeight: riskWeight,
        evidenceTypes: templateData?.evidence_types || template?.evidence_types || [],
        passFailCriteria: passFailCriteria,
        jurisdiction: templateData?.jurisdiction || template?.jurisdiction || '',
        jurisdictionNotes: jurisdictionNotes,
        ownerRole: templateData?.owner_role || template?.owner_role || 'manager',
        leadTimeDays: templateData?.lead_times_days?.[0] || template?.lead_times_days?.[0] || 30,
        dueDate: dueDate,
        createdAt: convertTimestamp(task.createdAt),
        updatedAt: convertTimestamp(task.updatedAt),
        templateSnapshot: snapshot || (template ? buildTemplateSnapshotFromTemplate(template) : null),
        assetName: assetName
      });
    }
    
    // Sort by due date
    enrichedTasks.sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());
    
    console.log(`✅ Enriched ${enrichedTasks.length} tasks with template and location details`);
    return enrichedTasks;
    
  } catch (error: any) {
    console.error('Error fetching tasks for organization:', error);
    // Handle auth errors (token expiration, etc.)
    await handleFirebaseAuthError(error);
    return [];
  }
}

// Get all tasks from the tasks table
export async function getAllTasks(): Promise<TaskWithDetails[]> {
  try {
    console.log('📋 Fetching tasks from tasks table...');
    
    // Get all tasks
    const tasksRef = collection(db, 'tasks');
    const tasksQuery = query(tasksRef, orderBy('createdAt', 'desc'));
    const tasksSnapshot = await getDocs(tasksQuery);
    
    const tasks: Task[] = [];
    tasksSnapshot.forEach((doc) => {
      tasks.push({ id: doc.id, ...doc.data() } as Task);
    });
    
    console.log(`📊 Found ${tasks.length} tasks in database`);
    
    // Get all templates and locations for enrichment
    const templatesResult = await complianceService.getAllTemplates();
    const locationsResult = await locationService.getAllLocations();
    
    if (!templatesResult.success || !locationsResult.success) {
      console.error('Failed to fetch templates or locations');
      return [];
    }
    
    let templates = templatesResult.data || [];
    const locations = locationsResult.data || [];
    
    // Backward compatibility: ensure all templates have risk_weight
    const { calculateRiskWeight } = await import('@/firebase/complianceService');
    templates = templates.map(template => {
      if (template.risk_weight === undefined || template.risk_weight === null) {
        return {
          ...template,
          risk_weight: calculateRiskWeight(template.risk_level)
        };
      }
      return template;
    });

    const templateCache = new Map<string, (typeof templates[number] | TemplateSnapshot) | null>();
    const resolveTemplate = async (templateId?: string, snapshot?: TemplateSnapshot | null) => {
      if (!templateId) return null;
      if (templateCache.has(templateId)) {
        return templateCache.get(templateId) || null;
      }
      if (snapshot) {
        templateCache.set(templateId, snapshot);
        return snapshot;
      }
      let template = templates.find(t => t.id === templateId);
      if (!template) {
        try {
          const result = await complianceService.getTemplate(templateId);
          if (result.success && result.data) {
            template = result.data;
            templates.push(template);
            templateCache.set(templateId, template);
          }
        } catch (err) {
          console.warn(`Failed to fetch template ${templateId}:`, err);
        }
      }
      templateCache.set(templateId, template || null);
      return template || null;
    };
    
    // Helper function to update task status in database if needed
    const updateTaskStatusIfNeeded = async (taskId: string, newStatus: 'overdue') => {
      try {
        const taskRef = doc(db, 'tasks', taskId);
        await updateDoc(taskRef, {
          status: newStatus,
          updatedAt: serverTimestamp()
        });
        console.log(`✅ Updated task ${taskId} status to ${newStatus}`);
      } catch (error) {
        console.error(`Error updating task ${taskId} status:`, error);
      }
    };

    // Collect all assetIds from assetIds array and checklistContext for batch fetching
    const assetIdsToFetch = new Set<string>();
    tasks.forEach(task => {
      if (task.assetIds && Array.isArray(task.assetIds)) {
        task.assetIds.forEach((id: string) => assetIdsToFetch.add(id));
      }
      if (task.checklistContext?.assetId) {
        assetIdsToFetch.add(task.checklistContext.assetId);
      }
    });
    
    // Fetch all assets in batch
    const assetMap = new Map<string, string>();
    if (assetIdsToFetch.size > 0) {
      const assetFetchPromises = Array.from(assetIdsToFetch).map(async (assetId) => {
        try {
          const assetResult = await assetService.getAsset(assetId);
          if (assetResult.success && assetResult.data) {
            assetMap.set(assetId, assetResult.data.assetName || `Asset ${assetId}`);
          }
        } catch (error) {
          console.warn(`Failed to fetch asset ${assetId}:`, error);
        }
      });
      await Promise.all(assetFetchPromises);
    }

    // Enrich tasks with template and location details
    const now = new Date();
    const enrichedTasks: TaskWithDetails[] = [];
    
    for (const task of tasks) {
      const snapshot = task.templateSnapshot;
      let template = await resolveTemplate(task.templateId, snapshot);
      if (!template && task.originalTemplateId) {
        template = await resolveTemplate(task.originalTemplateId, snapshot);
      }
      const templateData = (template || snapshot) as (Partial<TemplateSnapshot> & Partial<ComplianceTemplate>) | undefined;
      const location = locations.find(l => l.id === task.locationId);
      
      const dueDate = convertTimestamp(task.dueDate);
      
      // Dynamically calculate status: if dueDate has passed and task is still pending, mark as overdue
      let taskStatus = task.status;
      if (taskStatus === 'pending' && dueDate < now) {
        taskStatus = 'overdue';
        // Update task in database if status needs to change (async, don't await to avoid blocking)
        updateTaskStatusIfNeeded(task.id, 'overdue').catch(err => {
          console.error(`Error updating task ${task.id} status to overdue:`, err);
        });
      }
      
      // Calculate risk weight for scoring
      const riskLevel = (templateData?.risk_level || template?.risk_level || 'Medium') as string;
      const riskWeight = templateData?.risk_weight ?? (template?.risk_weight ?? calculateRiskWeight(riskLevel));
      
      // Get user language preference for translation
      let language = 'en';
      try {
        // Try to get language from localStorage as fallback (in getAllTasks, we don't have userId)
        const storedLang = localStorage.getItem('preferredLanguage');
        if (storedLang === 'es') language = 'es';
      } catch (error) {
        // Ignore localStorage errors
      }
      
      // Get translated values based on language
      const templateName = getTranslatedField(
        templateData?.name || template?.name,
        templateData?.name_es || template?.name_es,
        language
      ) || `Template ${task.templateId}`;
      
      const passFailCriteria = getTranslatedField(
        templateData?.pass_fail_criteria || template?.pass_fail_criteria,
        templateData?.pass_fail_criteria_es || template?.pass_fail_criteria_es,
        language
      );
      
      const jurisdictionNotes = getTranslatedField(
        templateData?.jurisdiction_notes || template?.jurisdiction_notes,
        templateData?.jurisdiction_notes_es || template?.jurisdiction_notes_es,
        language
      );
      
      // Get asset name from checklistContext if available
      const primaryAssetId = (task.assetIds && task.assetIds[0]) || task.checklistContext?.assetId;
      const assetName = primaryAssetId ? assetMap.get(primaryAssetId) : undefined;
      
      enrichedTasks.push({
        ...task,
        status: taskStatus,
        templateName: templateName,
        locationName: location?.name || `Location ${task.locationId}`,
        locationState: location?.state,
        category: templateData?.category || template?.category || 'Compliance',
        riskLevel: (templateData?.risk_level || template?.risk_level || 'medium') as string,
        riskWeight: riskWeight,
        evidenceTypes: templateData?.evidence_types || template?.evidence_types || [],
        passFailCriteria: passFailCriteria,
        jurisdiction: templateData?.jurisdiction || template?.jurisdiction || '',
        jurisdictionNotes: jurisdictionNotes,
        ownerRole: templateData?.owner_role || template?.owner_role || 'manager',
        leadTimeDays: templateData?.lead_times_days?.[0] || template?.lead_times_days?.[0] || 30,
        dueDate: dueDate,
        createdAt: convertTimestamp(task.createdAt),
        updatedAt: convertTimestamp(task.updatedAt),
        templateSnapshot: snapshot || (template ? buildTemplateSnapshotFromTemplate(template) : null),
        assetName: assetName
      });
    }
    
    // Sort by due date
    enrichedTasks.sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());
    
    console.log(`✅ Enriched ${enrichedTasks.length} tasks with template and location details`);
    return enrichedTasks;
    
  } catch (error: any) {
    console.error('Error fetching tasks:', error);
    // Handle auth errors (token expiration, etc.)
    await handleFirebaseAuthError(error);
    return [];
  }
}

// Get tasks tagged to a specific asset (assetIds array or checklistContext.assetId for backward compat)
export async function getTasksByAssetId(assetId: string): Promise<TaskWithDetails[]> {
  try {
    console.log(`🔍 Fetching tasks for asset ${assetId}...`);
    const tasksRef = collection(db, 'tasks');
    const seenIds = new Set<string>();
    const tasks: Task[] = [];

    // Query 1: tasks with assetIds array containing this asset (indexed)
    try {
      const q1 = query(
        tasksRef,
        where('assetIds', 'array-contains', assetId),
        orderBy('createdAt', 'desc')
      );
      const snap1 = await getDocs(q1);
      snap1.forEach((d) => {
        const taskData = { id: d.id, ...d.data() } as Task;
        if (!seenIds.has(taskData.id)) {
          seenIds.add(taskData.id);
          tasks.push(taskData);
        }
      });
    } catch (indexError: any) {
      if (indexError?.code === 'failed-precondition' || indexError?.message?.includes('index')) {
        console.warn('Tasks by assetIds index may be missing; falling back to full scan for assetIds.', indexError.message);
        const fallbackQuery = query(tasksRef, orderBy('createdAt', 'desc'));
        const fallbackSnap = await getDocs(fallbackQuery);
        fallbackSnap.forEach((d) => {
          const taskData = { id: d.id, ...d.data() } as Task;
          if (taskData.assetIds && Array.isArray(taskData.assetIds) && taskData.assetIds.includes(assetId) && !seenIds.has(taskData.id)) {
            seenIds.add(taskData.id);
            tasks.push(taskData);
          }
        });
      } else {
        throw indexError;
      }
    }

    // Query 2: backward compat – tasks with checklistContext.assetId (no assetIds set yet)
    try {
      const q2 = query(
        tasksRef,
        where('checklistContext.assetId', '==', assetId),
        orderBy('createdAt', 'desc')
      );
      const snap2 = await getDocs(q2);
      snap2.forEach((d) => {
        const taskData = { id: d.id, ...d.data() } as Task;
        if (!seenIds.has(taskData.id)) {
          seenIds.add(taskData.id);
          tasks.push(taskData);
        }
      });
    } catch (nestedIndexError: any) {
      if (nestedIndexError?.code === 'failed-precondition' || nestedIndexError?.message?.includes('index')) {
        console.warn('checklistContext.assetId index may be missing; skipping backward-compat query.');
      } else {
        throw nestedIndexError;
      }
    }

    // Sort by createdAt desc (in case we merged two lists)
    tasks.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.()?.getTime() ?? (typeof a.createdAt === 'number' ? a.createdAt : 0);
      const bTime = b.createdAt?.toDate?.()?.getTime() ?? (typeof b.createdAt === 'number' ? b.createdAt : 0);
      return bTime - aTime;
    });

    console.log(`📊 Found ${tasks.length} tasks for asset ${assetId}`);
    const enrichedTasks = await enrichTasks(tasks, undefined);
    return enrichedTasks;
  } catch (error: any) {
    console.error('Error fetching tasks by asset ID:', error);
    await handleFirebaseAuthError(error);
    return [];
  }
}

// Get count of open tasks (pending, overdue, pending_review) for an asset
export async function getOpenTaskCountByAssetId(assetId: string): Promise<number> {
  try {
    const tasksRef = collection(db, 'tasks');
    const openStatuses = ['pending', 'overdue', 'pending_review'];
    const tasks: Task[] = [];

    try {
      const q = query(
        tasksRef,
        where('assetIds', 'array-contains', assetId),
        where('status', 'in', openStatuses)
      );
      const snap = await getDocs(q);
      snap.forEach((d) => tasks.push({ id: d.id, ...d.data() } as Task));
    } catch (indexError: any) {
      if (indexError?.code === 'failed-precondition' || indexError?.message?.includes('index')) {
        const byAsset = await getTasksByAssetId(assetId);
        return byAsset.filter((t) => openStatuses.includes(t.status)).length;
      }
      throw indexError;
    }

    return tasks.length;
  } catch (error: any) {
    console.error('Error getting open task count by asset ID:', error);
    await handleFirebaseAuthError(error);
    return 0;
  }
}

// Helper function to get user preferred language
async function getUserLanguage(userId?: string): Promise<string> {
  if (!userId) return 'en';
  
  try {
    const { userService } = await import('@/firebase/database');
    const userResult = await userService.getUser(userId);
    if (userResult.success && userResult.data) {
      const preferredLanguage = userResult.data.preferredLanguage as string | undefined;
      return preferredLanguage === 'es' ? 'es' : 'en';
    }
  } catch (error) {
    console.warn('Failed to get user language, defaulting to English:', error);
  }
  
  // Check localStorage as fallback
  try {
    const storedLang = localStorage.getItem('preferredLanguage');
    if (storedLang === 'es') return 'es';
  } catch (error) {
    // Ignore localStorage errors
  }
  
  return 'en';
}

// Helper function to get translated field value (similar to checklist sanitizeItemForLanguage)
function getTranslatedField(
  englishValue: string | undefined,
  spanishValue: string | undefined,
  language: string
): string {
  if (!englishValue) return '';
  
  if (language === 'es') {
    // Use Spanish if available, otherwise fall back to English
    return (spanishValue && spanishValue.trim().length > 0) ? spanishValue : englishValue;
  } else {
    // Use English
    return englishValue;
  }
}

// Helper function to enrich tasks (extracted from getAllTasks)
async function enrichTasks(tasks: Task[], userId?: string): Promise<TaskWithDetails[]> {
  // Get user language preference
  const language = await getUserLanguage(userId);
  try {
    // Get all templates and locations for enrichment
    const templatesResult = await complianceService.getAllTemplates();
    const locationsResult = await locationService.getAllLocations();
    
    if (!templatesResult.success || !locationsResult.success) {
      console.error('Failed to fetch templates or locations');
      return [];
    }
    
    let templates = templatesResult.data || [];
    const locations = locationsResult.data || [];
    
    // Backward compatibility: ensure all templates have risk_weight
    const { calculateRiskWeight } = await import('@/firebase/complianceService');
    templates = templates.map(template => {
      if (template.risk_weight === undefined || template.risk_weight === null) {
        return {
          ...template,
          risk_weight: calculateRiskWeight(template.risk_level)
        };
      }
      return template;
    });

    const templateCache = new Map<string, (typeof templates[number] | TemplateSnapshot) | null>();
    const resolveTemplate = async (templateId?: string, snapshot?: TemplateSnapshot | null) => {
      if (!templateId) return null;
      if (templateCache.has(templateId)) {
        return templateCache.get(templateId) || null;
      }
      if (snapshot) {
        templateCache.set(templateId, snapshot);
        return snapshot;
      }
      let template = templates.find(t => t.id === templateId);
      if (!template) {
        try {
          const result = await complianceService.getTemplate(templateId);
          if (result.success && result.data) {
            template = result.data;
            templates.push(template);
            templateCache.set(templateId, template);
          }
        } catch (err) {
          console.warn(`Failed to fetch template ${templateId}:`, err);
        }
      }
      templateCache.set(templateId, template || null);
      return template || null;
    };
    
    // Convert Firestore timestamp to Date
    const convertTimestamp = (timestamp: any): Date => {
      if (timestamp && timestamp.toDate) {
        return timestamp.toDate();
      } else if (timestamp && timestamp._seconds) {
        return new Date(timestamp._seconds * 1000);
      } else if (timestamp instanceof Date) {
        return timestamp;
      } else if (typeof timestamp === 'number') {
        return new Date(timestamp);
      }
      return new Date();
    };

    // Collect all assetIds from assetIds array and checklistContext for batch fetching
    const assetIdsToFetch = new Set<string>();
    tasks.forEach(task => {
      if (task.assetIds && Array.isArray(task.assetIds)) {
        task.assetIds.forEach((id: string) => assetIdsToFetch.add(id));
      }
      if (task.checklistContext?.assetId) {
        assetIdsToFetch.add(task.checklistContext.assetId);
      }
    });
    
    // Fetch all assets in batch
    const assetMap = new Map<string, string>();
    if (assetIdsToFetch.size > 0) {
      const assetFetchPromises = Array.from(assetIdsToFetch).map(async (assetId) => {
        try {
          const assetResult = await assetService.getAsset(assetId);
          if (assetResult.success && assetResult.data) {
            assetMap.set(assetId, assetResult.data.assetName || `Asset ${assetId}`);
          }
        } catch (error) {
          console.warn(`Failed to fetch asset ${assetId}:`, error);
        }
      });
      await Promise.all(assetFetchPromises);
    }

    // Enrich tasks with template and location details
    const now = new Date();
    const enrichedTasks: TaskWithDetails[] = [];
    
    for (const task of tasks) {
      const snapshot = task.templateSnapshot;
      let template = await resolveTemplate(task.templateId, snapshot);
      if (!template && task.originalTemplateId) {
        template = await resolveTemplate(task.originalTemplateId, snapshot);
      }
      const templateData = (template || snapshot) as (Partial<TemplateSnapshot> & Partial<ComplianceTemplate>) | undefined;
      const location = locations.find(l => l.id === task.locationId);
      
      const dueDate = convertTimestamp(task.dueDate);
      
      // Dynamically calculate status: if dueDate has passed and task is still pending, mark as overdue
      let taskStatus = task.status;
      if (taskStatus === 'pending' && dueDate < now) {
        taskStatus = 'overdue';
      }
      
      // Calculate risk weight for scoring
      const riskLevel = (templateData?.risk_level || template?.risk_level || 'Medium') as string;
      const riskWeight = templateData?.risk_weight ?? (template?.risk_weight ?? calculateRiskWeight(riskLevel));
      
      // Get translated values based on language
      const templateName = getTranslatedField(
        templateData?.name || template?.name,
        templateData?.name_es || template?.name_es,
        language
      ) || `Template ${task.templateId}`;
      
      const passFailCriteria = getTranslatedField(
        templateData?.pass_fail_criteria || template?.pass_fail_criteria,
        templateData?.pass_fail_criteria_es || template?.pass_fail_criteria_es,
        language
      );
      
      const jurisdictionNotes = getTranslatedField(
        templateData?.jurisdiction_notes || template?.jurisdiction_notes,
        templateData?.jurisdiction_notes_es || template?.jurisdiction_notes_es,
        language
      );
      
      // Get asset name from checklistContext if available
      const primaryAssetId = (task.assetIds && task.assetIds[0]) || task.checklistContext?.assetId;
      const assetName = primaryAssetId ? assetMap.get(primaryAssetId) : undefined;
      
      enrichedTasks.push({
        ...task,
        status: taskStatus,
        templateName: templateName,
        locationName: location?.name || `Location ${task.locationId}`,
        locationState: location?.state,
        category: templateData?.category || template?.category || 'Compliance',
        riskLevel: (templateData?.risk_level || template?.risk_level || 'medium') as string,
        riskWeight: riskWeight,
        evidenceTypes: templateData?.evidence_types || template?.evidence_types || [],
        passFailCriteria: passFailCriteria,
        jurisdiction: templateData?.jurisdiction || template?.jurisdiction || '',
        jurisdictionNotes: jurisdictionNotes,
        ownerRole: templateData?.owner_role || template?.owner_role || 'manager',
        leadTimeDays: templateData?.lead_times_days?.[0] || template?.lead_times_days?.[0] || 30,
        dueDate: dueDate,
        createdAt: convertTimestamp(task.createdAt),
        updatedAt: convertTimestamp(task.updatedAt),
        templateSnapshot: snapshot || (template ? buildTemplateSnapshotFromTemplate(template) : null),
        assetName: assetName
      });
    }
    
    return enrichedTasks;
  } catch (error) {
    console.error('Error enriching tasks:', error);
    return [];
  }
}

// Get tasks for a specific user (filtered by their assigned locations)
export async function getTasksForUser(userId: string, userLocationIds: string[]): Promise<TaskWithDetails[]> {
  try {
    console.log(`👤 Fetching tasks for user ${userId} with locations:`, userLocationIds);
    
    if (!userLocationIds || userLocationIds.length === 0) {
      console.log('No locations assigned to user');
      return [];
    }
    
    // Get tasks for user's locations
    const tasksRef = collection(db, 'tasks');
    const tasksQuery = query(
      tasksRef, 
      where('locationId', 'in', userLocationIds),
      orderBy('createdAt', 'desc')
    );
    const tasksSnapshot = await getDocs(tasksQuery);
    
    const tasks: Task[] = [];
    tasksSnapshot.forEach((doc) => {
      tasks.push({ id: doc.id, ...doc.data() } as Task);
    });
    
    console.log(`📊 Found ${tasks.length} tasks for user's locations`);
    
    // Get all templates and locations for enrichment
    const templatesResult = await complianceService.getAllTemplates();
    const locationsResult = await locationService.getAllLocations();
    
    if (!templatesResult.success || !locationsResult.success) {
      console.error('Failed to fetch templates or locations');
      return [];
    }
    
    let templates = templatesResult.data || [];
    const locations = locationsResult.data || [];
    
    // Backward compatibility: ensure all templates have risk_weight
    const { calculateRiskWeight } = await import('@/firebase/complianceService');
    templates = templates.map(template => {
      if (template.risk_weight === undefined || template.risk_weight === null) {
        return {
          ...template,
          risk_weight: calculateRiskWeight(template.risk_level)
        };
      }
      return template;
    });
    
    const templateCache = new Map<string, (typeof templates[number] | TemplateSnapshot) | null>();
    const resolveTemplate = async (templateId?: string, snapshot?: TemplateSnapshot | null) => {
      if (!templateId) return null;
      if (templateCache.has(templateId)) {
        return templateCache.get(templateId) || null;
      }
      if (snapshot) {
        templateCache.set(templateId, snapshot);
        return snapshot;
      }
      let template = templates.find(t => t.id === templateId);
      if (!template) {
        try {
          const templateResult = await complianceService.getTemplate(templateId);
          if (templateResult.success && templateResult.data) {
            template = templateResult.data;
            templates.push(template);
            templateCache.set(templateId, template);
          }
        } catch (err) {
          console.warn(`Failed to fetch template ${templateId}:`, err);
        }
      }
      templateCache.set(templateId, template || null);
      return template || null;
    };
    
    // Collect all assetIds from assetIds array and checklistContext for batch fetching
    const assetIdsToFetch = new Set<string>();
    tasks.forEach(task => {
      if (task.assetIds && Array.isArray(task.assetIds)) {
        task.assetIds.forEach((id: string) => assetIdsToFetch.add(id));
      }
      if (task.checklistContext?.assetId) {
        assetIdsToFetch.add(task.checklistContext.assetId);
      }
    });
    
    // Fetch all assets in batch
    const assetMap = new Map<string, string>();
    if (assetIdsToFetch.size > 0) {
      const assetFetchPromises = Array.from(assetIdsToFetch).map(async (assetId) => {
        try {
          const assetResult = await assetService.getAsset(assetId);
          if (assetResult.success && assetResult.data) {
            assetMap.set(assetId, assetResult.data.assetName || `Asset ${assetId}`);
          }
        } catch (error) {
          console.warn(`Failed to fetch asset ${assetId}:`, error);
        }
      });
      await Promise.all(assetFetchPromises);
    }
    
    // Enrich tasks with template and location details
    const enrichedTasks: TaskWithDetails[] = [];
    for (const task of tasks) {
      const snapshot = task.templateSnapshot;
      let template = await resolveTemplate(task.templateId, snapshot);
      if (!template && task.originalTemplateId) {
        template = await resolveTemplate(task.originalTemplateId, snapshot);
      }
      const templateData = (template || snapshot) as (Partial<TemplateSnapshot> & Partial<ComplianceTemplate>) | undefined;
      const location = locations.find(l => l.id === task.locationId);
      
      const dueDate = convertTimestamp(task.dueDate);
      // Calculate risk weight for scoring (using already imported calculateRiskWeight)
      const riskLevel = (templateData?.risk_level || template?.risk_level || 'Medium') as string;
      const riskWeight = templateData?.risk_weight ?? (template?.risk_weight ?? calculateRiskWeight(riskLevel));
      
      // Get user language preference for translation
      const language = await getUserLanguage(userId);
      
      // Get translated values based on language
      const templateName = getTranslatedField(
        templateData?.name || template?.name,
        templateData?.name_es || template?.name_es,
        language
      ) || `Template ${task.templateId}`;
      
      const passFailCriteria = getTranslatedField(
        templateData?.pass_fail_criteria || template?.pass_fail_criteria,
        templateData?.pass_fail_criteria_es || template?.pass_fail_criteria_es,
        language
      );
      
      const jurisdictionNotes = getTranslatedField(
        templateData?.jurisdiction_notes || template?.jurisdiction_notes,
        templateData?.jurisdiction_notes_es || template?.jurisdiction_notes_es,
        language
      );
      
      // Get asset name from checklistContext if available
      const primaryAssetId = (task.assetIds && task.assetIds[0]) || task.checklistContext?.assetId;
      const assetName = primaryAssetId ? assetMap.get(primaryAssetId) : undefined;
      
      enrichedTasks.push({
        ...task,
        templateName: templateName,
        locationName: location?.name || `Location ${task.locationId}`,
        locationState: location?.state,
        category: templateData?.category || template?.category || 'Compliance',
        riskLevel: (templateData?.risk_level || template?.risk_level || 'medium') as string,
        riskWeight,
        evidenceTypes: templateData?.evidence_types || template?.evidence_types || [],
        passFailCriteria: passFailCriteria,
        jurisdiction: templateData?.jurisdiction || template?.jurisdiction || '',
        jurisdictionNotes: jurisdictionNotes,
        ownerRole: templateData?.owner_role || template?.owner_role || 'manager',
        leadTimeDays: templateData?.lead_times_days?.[0] || template?.lead_times_days?.[0] || 30,
        dueDate,
        createdAt: convertTimestamp(task.createdAt),
        updatedAt: convertTimestamp(task.updatedAt),
        templateSnapshot: snapshot || (template ? buildTemplateSnapshotFromTemplate(template) : null),
        assetName: assetName
    });
    }
    
    // Sort by due date
    enrichedTasks.sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());
    
    console.log(`✅ Enriched ${enrichedTasks.length} tasks for user`);
    return enrichedTasks;
    
  } catch (error: any) {
    console.error('Error fetching tasks for user:', error);
    // Handle auth errors (token expiration, etc.)
    await handleFirebaseAuthError(error);
    return [];
  }
}

// Mark a task as completed
export async function completeTask(taskId: string, userId: string): Promise<{ success: boolean; error?: string }> {
  try {
    console.log(`✅ Marking task ${taskId} as completed by user ${userId}`);
    console.log(`🔍 Database instance:`, db);
    console.log(`🔍 Task ID format:`, typeof taskId, taskId);
    console.log(`🔍 User ID format:`, typeof userId, userId);
    
    // Validate inputs
    if (!taskId) {
      console.error('❌ Task ID is required');
      return { success: false, error: 'Task ID is required' };
    }
    
    if (!userId) {
      console.error('❌ User ID is required');
      return { success: false, error: 'User ID is required' };
    }
    
    // First, let's check if the task exists
    const taskRef = doc(db, 'tasks', taskId);
    console.log(`🔍 Task reference created:`, taskRef.path);
    
    // Try to get the task first to see if it exists
    const taskDoc = await getDoc(taskRef);
    
    if (!taskDoc.exists()) {
      console.error(`❌ Task ${taskId} does not exist in database`);
      return { success: false, error: `Task ${taskId} does not exist` };
    }
    
    console.log(`✅ Task exists, current data:`, taskDoc.data());
    
    await updateDoc(taskRef, {
      status: 'pending_review',
      completedBy: userId,
      completedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    console.log(`✅ Task ${taskId} marked as pending review`);
    return { success: true };
    
  } catch (error: any) {
    console.error('❌ Error completing task:', error);
    console.error('❌ Error code:', error.code);
    console.error('❌ Error message:', error.message);
    console.error('❌ Full error:', error);
    return { success: false, error: error.message };
  }
}

// Get task completion details from taskCompletions collection
export async function getTaskCompletionDetails(taskId: string): Promise<any> {
  try {
    const completionsRef = collection(db, 'taskCompletions');
    // Query without orderBy to avoid requiring a composite index
    // We'll sort in memory instead
    const q = query(
      completionsRef,
      where('taskId', '==', taskId)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    // Sort completions by completionDate in memory (descending)
    const completions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by completionDate descending (most recent first)
    completions.sort((a: any, b: any) => {
      const dateA = a.completionDate?.toDate ? a.completionDate.toDate().getTime() : 
                    (a.completionDate instanceof Date ? a.completionDate.getTime() : 
                     (typeof a.completionDate === 'number' ? a.completionDate : 0));
      const dateB = b.completionDate?.toDate ? b.completionDate.toDate().getTime() : 
                    (b.completionDate instanceof Date ? b.completionDate.getTime() : 
                     (typeof b.completionDate === 'number' ? b.completionDate : 0));
      return dateB - dateA; // Descending order
    });
    
    // Return the most recent completion
    return completions[0] || null;
    
  } catch (error) {
    console.error('Error getting task completion details:', error);
    return null;
  }
}

// Review a task (only admins and managers can do this)
export async function reviewTask(taskId: string, reviewerId: string, reviewerEmail: string): Promise<{ success: boolean; error?: string }> {
  try {
    console.log(`👁️ Reviewing task ${taskId} by ${reviewerId}`);
    
    if (!taskId) {
      return { success: false, error: 'Task ID is required' };
    }
    
    if (!reviewerId) {
      return { success: false, error: 'Reviewer ID is required' };
    }
    
    const taskRef = doc(db, 'tasks', taskId);
    const taskDoc = await getDoc(taskRef);
    
    if (!taskDoc.exists()) {
      return { success: false, error: `Task ${taskId} does not exist` };
    }
    
    const taskData = taskDoc.data();
    
    // Check if task is in pending_review status
    if (taskData.status !== 'pending_review') {
      return { success: false, error: `Task is not in pending_review status. Current status: ${taskData.status}` };
    }
    
    await updateDoc(taskRef, {
      status: 'completed',
      reviewedBy: reviewerEmail,
      reviewedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    console.log(`✅ Task ${taskId} reviewed and marked as completed`);
    return { success: true };
    
  } catch (error: any) {
    console.error('❌ Error reviewing task:', error);
    return { success: false, error: error.message };
  }
}

// Reject a task (send it back to user) - only admins and managers can do this
export async function rejectTask(
  taskId: string, 
  reviewerId: string, 
  reviewerEmail: string, 
  rejectionReason: string
): Promise<{ success: boolean; error?: string }> {
  try {
    console.log(`❌ Rejecting task ${taskId} by ${reviewerId}`);
    
    if (!taskId) {
      return { success: false, error: 'Task ID is required' };
    }
    
    if (!reviewerId) {
      return { success: false, error: 'Reviewer ID is required' };
    }
    
    if (!rejectionReason || rejectionReason.trim().length === 0) {
      return { success: false, error: 'Rejection reason is required' };
    }
    
    const taskRef = doc(db, 'tasks', taskId);
    const taskDoc = await getDoc(taskRef);
    
    if (!taskDoc.exists()) {
      return { success: false, error: `Task ${taskId} does not exist` };
    }
    
    const taskData = taskDoc.data();
    
    // Check if task is in pending_review status
    if (taskData.status !== 'pending_review') {
      return { success: false, error: `Task is not in pending_review status. Current status: ${taskData.status}` };
    }
    
    // Determine new status: if due date has passed, set to 'overdue', otherwise 'pending'
    const dueDate = taskData.dueDate?.toDate ? taskData.dueDate.toDate() : 
                    (taskData.dueDate instanceof Date ? taskData.dueDate :
                    (typeof taskData.dueDate === 'number' ? new Date(taskData.dueDate) : null));
    
    const now = new Date();
    const newStatus = (dueDate && dueDate < now) ? 'overdue' : 'pending';
    
    await updateDoc(taskRef, {
      status: newStatus,
      rejectedBy: reviewerEmail,
      rejectedAt: serverTimestamp(),
      rejectionReason: rejectionReason.trim(),
      updatedAt: serverTimestamp()
    });
    
    // Mark the task completion record as rejected (if it exists)
    try {
      const completionsResult = await taskCompletionService.getCompletionsForTask(taskId);
      if (completionsResult.success && completionsResult.data && completionsResult.data.length > 0) {
        // Get the most recent completion (should be the one in pending_review)
        const latestCompletion = completionsResult.data[0];
        if (latestCompletion.id) {
          await taskCompletionService.updateCompletion(latestCompletion.id, {
            rejected: true,
            rejectedBy: reviewerEmail,
            rejectedAt: serverTimestamp(),
            rejectionReason: rejectionReason.trim(),
            updatedAt: serverTimestamp()
          });
          console.log(`✅ Marked completion ${latestCompletion.id} as rejected`);
        }
      }
    } catch (completionError) {
      // Log error but don't fail the rejection if completion update fails
      console.warn('⚠️ Failed to mark completion as rejected (non-critical):', completionError);
    }
    
    console.log(`✅ Task ${taskId} rejected and set back to ${newStatus} status`);
    return { success: true };
    
  } catch (error: any) {
    console.error('❌ Error rejecting task:', error);
    return { success: false, error: error.message };
  }
}

// Get user role from Firestore
export async function getUserRole(userId: string): Promise<string | null> {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return userDoc.data().role || null;
    }
    
    return null;
  } catch (error: any) {
    console.error('Error getting user role:', error);
    // Handle auth errors (token expiration, etc.)
    await handleFirebaseAuthError(error);
    return null;
  }
}
